<template>
  <teleport to="body">
    <transition name="popup-slide">
      <div
        v-if="isVisible"
        class="fixed inset-0 z-[8500] flex items-center justify-center p-4"
        @click.self="dismiss"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="dismiss"></div>

        <!-- Popup card -->
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
             role="dialog" aria-modal="true" aria-label="Ontvang €10 korting">

          <!-- Sluiten -->
          <button
            @click="dismiss"
            class="absolute top-3 right-3 z-10 text-white/60 hover:text-white transition-colors"
            aria-label="Sluiten"
          >
            <i class="bi bi-x-lg text-[1.1rem]"></i>
          </button>

          <!-- Donkere header -->
          <div class="bg-[#111] px-6 pt-8 pb-6 text-center">
            <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFD500]/15 mb-4">
              <i class="bi bi-tag-fill text-[#FFD500] text-[1.5rem]"></i>
            </div>
            <h2 class="text-white font-bold text-[1.3rem] leading-snug mb-1">
              €10 korting op je eerste bestelling
            </h2>
            <p class="text-white/60 text-[0.85rem] leading-relaxed">
              Laat je e-mail achter en ontvang direct een kortingscode.
            </p>
          </div>

          <!-- Formulier of succes -->
          <div class="px-6 py-6">

            <!-- Succes-state -->
            <div v-if="submitted" class="text-center py-2">
              <div class="w-12 h-12 rounded-full bg-[#dcfce7] flex items-center justify-center mx-auto mb-3">
                <i class="bi bi-check-lg text-[#22c55e] text-[1.3rem]"></i>
              </div>
              <p class="font-semibold text-[#111] mb-1">Je kortingscode:</p>
              <button
                @click="copyCode"
                class="w-full bg-[#f9fafb] border-2 border-dashed border-[#FFD500] rounded-xl py-3 px-4 mb-1 flex items-center justify-center gap-3 group transition-colors hover:bg-[#fffbeb]"
                :aria-label="copied ? 'Gekopieerd!' : 'Klik om te kopiëren'"
              >
                <span class="font-mono font-bold text-[1.2rem] tracking-widest text-[#111]">WELKOM10</span>
                <span class="text-[0.72rem] font-semibold text-[#aaa] group-hover:text-[#b8860b] transition-colors flex items-center gap-1">
                  <i :class="['bi', copied ? 'bi-check2 text-[#22c55e]' : 'bi-clipboard']"></i>
                  {{ copied ? 'Gekopieerd!' : 'Kopiëren' }}
                </span>
              </button>
              <p class="text-[#bbb] text-[0.75rem] mb-4">Klik om te kopiëren, gebruik hem in de checkout.</p>
              <button
                @click="dismiss"
                class="w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-xl transition-colors text-[0.9rem]"
              >
                Nu winkelen <i class="bi bi-arrow-right ml-1"></i>
              </button>
            </div>

            <!-- Formulier-state -->
            <form v-else @submit.prevent="submit">
              <input
                v-model="email"
                type="email"
                required
                placeholder="jouw@email.nl"
                class="w-full border border-gray-200 rounded-xl px-4 py-3 text-[0.9rem] text-[#111] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-[border-color,box-shadow] duration-150 mb-3"
              />
              <button
                type="submit"
                class="dp-submit w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-xl text-[0.9rem]"
              >
                Geef mij €10 korting
              </button>
              <p class="text-center text-[#bbb] text-[0.75rem] mt-3">
                Geen spam. Je kunt je altijd uitschrijven.
              </p>
            </form>

          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@stores/auth'

const auth = useAuthStore()

const isVisible = ref(false)
const submitted = ref(false)
const email     = ref('')
const copied    = ref(false)

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText('WELKOM10')
    copied.value = true
    setTimeout(() => { copied.value = false }, 2200)
  } catch {
    // fallback: selecteer tekst
  }
}

const STORAGE_KEY = 'p2c_discount_popup_dismissed'
const DELAY_MS    = 18000  // 18 seconden inactiviteit

let timer = null

const shouldShow = () => {
  if (auth.isLoggedIn) return false
  if (typeof window === 'undefined') return false
  if (localStorage.getItem(STORAGE_KEY)) return false
  return true
}

const show = () => {
  if (!shouldShow()) return
  isVisible.value = true
}

const dismiss = () => {
  isVisible.value = false
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, '1')
  }
}

const submit = () => {
  // Toon direct de kortingscode - verstuur de aanmelding best-effort op de
  // achtergrond, zodat de UX nooit blokkeert als de mail even faalt.
  submitted.value = true

  fetch('/api/newsletter-subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.value, source: 'discount_popup' }),
  }).catch(() => {})

  // Optioneel: track GA4 event
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'discount_popup_submitted', {
      event_category: 'conversion',
      email: email.value
    })
  }
}

// Exit intent: muis verlaat viewport aan de bovenkant
const handleMouseLeave = (e) => {
  if (e.clientY <= 0 && shouldShow()) {
    clearTimeout(timer)
    show()
    document.removeEventListener('mouseleave', handleMouseLeave)
  }
}

onMounted(() => {
  if (!shouldShow()) return

  // Timer: toon na DELAY_MS als gebruiker nog op de pagina is
  timer = setTimeout(() => {
    show()
    document.removeEventListener('mouseleave', handleMouseLeave)
  }, DELAY_MS)

  // Exit intent
  document.addEventListener('mouseleave', handleMouseLeave)
})

onUnmounted(() => {
  clearTimeout(timer)
  document.removeEventListener('mouseleave', handleMouseLeave)
})
</script>

<style scoped>
.popup-slide-enter-active,
.popup-slide-leave-active {
  transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
.popup-slide-enter-active .relative {
  transition: transform 280ms cubic-bezier(0.77, 0, 0.175, 1), opacity 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
.popup-slide-leave-active .relative {
  transition: transform 180ms cubic-bezier(0.23, 1, 0.32, 1), opacity 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.popup-slide-enter-from,
.popup-slide-leave-to {
  opacity: 0;
}
.popup-slide-enter-from .relative {
  transform: scale(0.94) translateY(12px);
  opacity: 0;
}

.dp-submit {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              box-shadow 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.dp-submit:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.4);
}
.dp-submit:active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) { .dp-submit { transition: none; } }
</style>
